<template>
  <div class="wager-card">
    <!-- Header Section (Dark Blue Background) -->
    <div class="card-header">
      <div class="header-left">
        <div class="status-row">
          <span class="status-label">Status:</span>
          <span :class="['status-value', getStatusClass(transaction)]">
            {{ transaction.bet_status }}
          </span>
        </div>
        <div class="date-time">{{ formatDate(transaction.created_at) }}</div>
      </div>
      <div class="header-right">
        <div class="bet-amount-row">
          <span class="bet-amount-label">Bet Amount:</span>
          <span class="bet-amount-value">{{ formatCurrency(transaction.total, transaction.currency_symbol) }}</span>
        </div>
        <div class="id-row">
          <span class="id-label">BETSLIP ID:</span>
          <span class="id-value">{{ transaction.id }}</span>
        </div>
      </div>
    </div>

    <!-- Details Section (White Background) -->
    <div class="card-details">
      <div class="detail-row">
        <span class="detail-label">Description:</span>
        <span class="detail-value">Wager</span>
      </div>
      <div class="detail-row">
        <span class="detail-label">Draw ID:</span>
        <span class="detail-value">{{ getDrawId() }}</span>
      </div>
      
      <div class="detail-row">
        <span class="detail-label">Total Return:</span>
        <span class="detail-value">{{ getTotalReturn(transaction) }}</span>
      </div>
    </div>

    <!-- Action Section -->
    <div class="card-action">
      <button 
        class="show-more-btn" 
        @click="handleViewDetails"
      >
        View details
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { defineProps, defineEmits } from 'vue'
import type { Transaction } from '@/stores/transactions'
import { formatCurrency } from '@/utils/currency'

const props = defineProps<{
  transaction: Transaction
}>()

const emit = defineEmits<{
  (e: 'view-details', transaction: Transaction): void
  (e: 'rebet'): void
}>()

const handleViewDetails = (event: Event) => {
  event.preventDefault()
  emit('view-details', props.transaction)
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');
  
  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
};

const getStatusClass = (transaction: Transaction) => {
  const statusLower = transaction.bet_status.toLowerCase()
  if (statusLower === 'open') return 'open'
  if (statusLower === 'settled' && Number(transaction.total_winnings || 0) <= 0) return 'settled-zero'
  if (statusLower === 'settled') return 'settled'
  if (statusLower === 'cancelled') return 'cancelled'
  return 'open'
}

const getTotalReturn = (transaction: Transaction) => {
  if (transaction.bet_status.toLowerCase() === 'open') {
    return 'TBC'
  }
  return formatCurrency(transaction.total_winnings, transaction.currency_symbol)
}

const getDrawId = () => {
  return props.transaction.reference ; 
}

</script>
<script lang="ts">
export default {
  name: 'WagerCard',
}
</script>
<style scoped>
.wager-card {
  background: var(--bg-card);
  border-radius: 8px;
  margin-bottom: 12px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

@media (max-width: 768px) {
  .wager-card {
    padding-left: 6px;
    padding-right: 6px;
    margin-bottom: 6px; /* Further reduce bottom margin */
  }

  /* Ultra-compact header */
  .card-header {
    padding: 6px 8px; /* Further reduce padding */
  }

  .header-left,
  .header-right {
    gap: 1px; /* Minimal gap */
  }

  /* Ultra-compact card details */
  .card-details {
    padding: 6px 8px; /* Further reduce padding */
  }

  .detail-row {
    padding: 2px 0; /* Minimal padding */
  }

  .detail-label {
    min-width: 80px; /* Further reduce min-width */
    font-size: 10px; /* Smaller font */
  }

  .detail-value {
    font-size: 10px; /* Smaller font */
  }

  /* Ultra-compact expanded content */
  .game-header {
    padding: 6px 8px; /* Further reduce padding */
  }

  .bet-details {
    padding: 6px 8px; /* Further reduce padding */
  }

  .bet-detail-row {
    padding: 1px 0; /* Minimal padding */
  }

  .bet-detail-label {
    min-width: 80px; /* Further reduce min-width */
    font-size: 10px; /* Smaller font */
  }

  .bet-detail-value {
    font-size: 10px; /* Smaller font */
  }


  .section-title {
    font-size: 12px; /* Smaller section title */
  }


  .bet-header {
    margin-bottom: 2px; /* Minimal margin */
    padding-bottom: 2px; /* Minimal padding */
  }

  .bet-details {
    gap: 2px; /* Minimal gap */
  }

  .market-info {
    gap: 1px; /* Minimal gap */
  }

  .market-name {
    font-size: 10px; /* Smaller font */
  }

  .market-text {
    font-size: 11px; /* Smaller font */
  }

  .bet-summary {
    gap: 1px; /* Minimal gap */
  }

  .summary-row {
    gap: 2px; /* Minimal gap */
  }

  .summary-label,
  .summary-value {
    font-size: 10px; /* Smaller font */
  }

  /* Ultra-compact expanded actions */
  .expanded-actions {
    padding: 4px 8px; /* Minimal padding */
    gap: 4px; /* Minimal gap */
  }

  .results-numbers {
    justify-content: flex-end;
    gap: 2px; /* Minimal gap between number balls */
  }

  .no-results-text {
    text-align: right; 
  }

  /* Remove unnecessary borders and spacing */
  .detail-row {
    border-bottom: none; /* Remove borders on mobile */
  }

  .bet-detail-row {
    border-bottom: none; /* Remove borders on mobile */
  }

  .bet-header {
    border-bottom: none; /* Remove borders on mobile */
  }
}

.card-header {
  background: var(--bg-dark);
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.header-left {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.header-right {
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: right;
}

.status-row {
  display: flex;
  align-items: center;
  gap: 4px;
}

.status-label,
.bet-amount-label,
.id-label {
  color: var(--text-white);
  font-size: 13.91px;
  font-weight: 500;
}

.status-value {
  font-size: 13.91px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
}

.status-value.open {
  color: var(--warning);
}

.status-value.settled {
  color: var(--success);
}

.status-value.settled-zero {
  color: var(--error);
}

.status-value.cancelled {
  color: var(--error);
}

.date-time,
.bet-amount-value,
.id-value {
  color: var(--text-white);
  font-size: 11px;
}

.card-details {
  padding: 12px 16px;
  background: var(--bg-card);
}

.detail-row {
  display: flex;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid var(--border-light, var(--border-color));
}


.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  color: var(--text-secondary);
  font-size: 12.19px;
  font-weight: 500;
  min-width: 120px;
  flex-shrink: 0;
}

.detail-value {
  color: var(--text-primary);
  font-size: 12.19px;
  font-weight: 500;
}

.card-action {
  /* padding: 12px 16px; */
  background: var(--bg-card);
  text-align: center;
  border-top: 1px solid var(--border-color);
}

.wager-card.expanded .card-action {
  border-top: none;
}

.show-more-btn {
  background: none;
  border: none;
  color: var(--text-primary);
  font-size: 12px;
  cursor: pointer;
  text-decoration: underline;
  padding: 10px 8px;
}

.expanded-content {
  background: var(--bg-card);
}

.game-header {
  background: var(--bg-dark);
  padding: 12px 16px;
  text-align: left;
}

.game-header h3 {
  color: var(--text-white);
  font-size: 14px;
  font-weight: 500;
  margin: 0;
}

.bet-detail-row {
  display: flex;
  align-items: center;
  /* padding: 8px 0; */
  border-bottom: 1px solid var(--border-light, var(--border-color));
}


.results-numbers {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-end;
}

.no-results-text {
  color: var(--text-secondary);
  font-size: 12px;
  font-style: italic;
}

.bet-detail-row:last-child {
  border-bottom: none;
}

.bet-detail-label {
  color: var(--text-secondary);
  font-size: 12px;
  font-weight: 500;
  min-width: 120px;
  flex-shrink: 0;
}

.bet-detail-value {
  color: var(--text-primary);
  font-size: 12px;
  font-weight: 500;
}

.your-bets-section {
  padding: 6px 8px;
  background: var(--bg-card);
}

.results-section {
  padding: 6px 8px;
  background: var(--bg-card);
}

.section-header,
.results-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-title {
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.bet-item {
  padding: 6px;
  background: var(--bg-secondary);
  border-radius: 8px;
  border: 1px solid var(--border-color);
}

.bet-item:last-child {
  margin-bottom: 0;
}

.bet-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border-color);
}

.bet-id {
  color: var(--text-primary);
  font-size: 12px;
  font-weight: 600;
}

.bet-status {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}

.bet-status.settled {
  background: var(--bg-success);
  color: var(--text-white);
}

.bet-status.open {
  background: var(--bg-warning);
  color: var(--text-primary);
}

.bet-status.cancelled {
  background: var(--bg-danger);
  color: var(--text-white);
}

.bet-details {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.market-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.market-name {
  color: var(--text-secondary);
  font-size: 12px;
  font-weight: 500;
}

.market-text {
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 600;
}

.bet-summary {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.summary-label {
  color: var(--text-secondary);
  font-size: 12px;
  font-weight: 500;
}

.summary-value {
  color: var(--text-primary);
  font-size: 12px;
  font-weight: 600;
}

.your-bet-label,
.results-label {
  color: var(--text-secondary);
  font-size: 12px;
  font-weight: 500;
  min-width: 120px;
  flex-shrink: 0;
}

.your-bet-description,
.results-description {
  color: var(--text-primary);
  font-size: 12px;
  font-weight: 500;
}

.selected-numbers,
.results-numbers {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-start;
  margin-top: 8px;
}

.no-results {
  margin-top: 12px;
  text-align: center;
}

.no-results-text {
  color: var(--text-secondary);
  font-size: 12px;
  font-style: italic;
}


.expanded-actions {
  padding: 16px;
  background: var(--bg-card);
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
}

.rebet-btn {
  background: var(--bg-success);
  color: var(--text-white);
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  max-width: 200px;
}

.show-less-btn {
  background: none;
  border: none;
  color: var(--text-secondary);
  font-size: 12px;
  cursor: pointer;
  text-decoration: underline;
}

/* Desktop styles - hide this component on desktop */
@media (min-width: 769px) {
  .wager-card {
    display: none;
  }
}
</style>
